import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { Sport, EVOpportunity } from '../types';
import EVTable from './EVTable';

interface EVOpportunitiesProps {
  sports: Sport[];

  selectedSports: string[];
  selectedMarkets: string[];
  selectedBookmakers: string[];
  selectedPlayerPropMarkets: string[];
  minEV: number;
  maxEV: number;
  minProbability: number;
  maxProbability: number;
  searchTerm: string;
  selectedGameStatus: string[];
  onOpenFilters: () => void;
  hasManuallySearched: boolean;
  setHasManuallySearched: (value: boolean) => void;
  oddsFormat: 'decimal' | 'american';
  setOddsFormat: (format: 'decimal' | 'american') => void;
  userTier?: 'free' | 'gold' | 'platinum';
}

export default function EVOpportunities({
  sports,

  selectedSports,
  selectedMarkets,
  selectedBookmakers,
  selectedPlayerPropMarkets,
  minEV,
  maxEV,
  minProbability,
  maxProbability,
  searchTerm,
  selectedGameStatus,
  onOpenFilters,
  hasManuallySearched,
  setHasManuallySearched,
  oddsFormat,
  setOddsFormat,
  userTier = 'free'
}: EVOpportunitiesProps) {
  const [opportunities, setOpportunities] = useState<EVOpportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<EVOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightSignatureRef = useRef<string | null>(null);
  const lastSignatureRef = useRef<string | null>(null);

  const loadOpportunities = useCallback(async (force = false) => {
    const sortedSports = [...selectedSports].sort();
    const sortedMarkets = [...selectedMarkets].sort();
    const sortedBookmakers = [...selectedBookmakers].sort();
    const sortedPlayerPropMarkets = [...selectedPlayerPropMarkets].sort();
    const signature = JSON.stringify({
      sports: sortedSports,
      markets: sortedMarkets,
      bookmakers: sortedBookmakers,
      playerPropMarkets: sortedPlayerPropMarkets,
      minEV,
      maxEV,
      minProbability,
      maxProbability,
      selectedGameStatus,
    });

    // Mark that user has manually searched
    if (force) {
      setHasManuallySearched(true);
    }

    if (!force) {
      if (inFlightSignatureRef.current === signature) {
        return;
      }
      if (lastSignatureRef.current === signature) {
        return;
      }
    }

    inFlightSignatureRef.current = signature;
    setLoading(true);
    setError(null);

    try {
      const response = await api.getEVOpportunities({
        sports: selectedSports,
        markets: selectedMarkets,
        regions: ['us'],
        oddsFormat: 'decimal', // API always returns decimal, we convert for display
        minEV,
        bookmakers: selectedBookmakers.length > 0 ? selectedBookmakers : undefined,
        includeLiveGames: selectedGameStatus.includes('live'),
        playerPropMarkets: selectedPlayerPropMarkets.length > 0 ? selectedPlayerPropMarkets : undefined,
      });

      setOpportunities(response.data);
      setFilteredOpportunities(response.data);

      lastSignatureRef.current = signature;


    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load +EV opportunities';
      setError(message);
    } finally {
      inFlightSignatureRef.current = null;
      setLoading(false);
    }
  }, [
    selectedSports,
    selectedMarkets,
    selectedBookmakers,
    selectedPlayerPropMarkets,
    selectedGameStatus,
    minEV,
    maxEV,
    minProbability,
    maxProbability,
    setHasManuallySearched,
  ]);

  // Don't auto-load opportunities on mount - require manual search
  // This prevents using credits immediately when switching to EV tab
  useEffect(() => {
    // Only auto-load if user has already done a manual search
    if (hasManuallySearched) {
      loadOpportunities();
    }
  }, [loadOpportunities, hasManuallySearched]);

  // Filter opportunities based on search term and game status
  useEffect(() => {
    let filtered = opportunities;

    // Filter by game status (live/upcoming)
    const now = new Date();
    filtered = filtered.filter(opp => {
      const commenceTime = new Date(opp.commence_time);
      const isLive = commenceTime < now;

      if (selectedGameStatus.includes('live') && selectedGameStatus.includes('upcoming')) {
        return true;
      } else if (selectedGameStatus.includes('live')) {
        return isLive;
      } else if (selectedGameStatus.includes('upcoming')) {
        return !isLive;
      }
      return false;
    });

    // Filter by EV range
    filtered = filtered.filter(opp => opp.ev_percentage <= maxEV);

    // Filter by probability range (convert to percentage)
    filtered = filtered.filter(opp => {
      const probPercent = opp.sharp_probability * 100;
      return probPercent >= minProbability && probPercent <= maxProbability;
    });

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(opp =>
        opp.home_team.toLowerCase().includes(term) ||
        opp.away_team.toLowerCase().includes(term) ||
        opp.sport_title?.toLowerCase().includes(term) ||
        opp.sport_key.toLowerCase().includes(term) ||
        opp.bookmaker_title.toLowerCase().includes(term)
      );
    }

    setFilteredOpportunities(filtered);
  }, [searchTerm, opportunities, selectedGameStatus, maxEV, minProbability, maxProbability]);

  const handleRefresh = () => {
    loadOpportunities(true);
  };

  const getActiveFiltersText = () => {
    const parts: string[] = [];

    if (selectedSports.includes('upcoming')) {
      parts.push('All Sports');
    } else {
      const sportNames = selectedSports.map(key =>
        sports.find(s => s.key === key)?.title || key
      );
      parts.push(sportNames.join(', '));
    }

    // Add markets display
    const MARKET_LABELS: Record<string, string> = {
      'h2h': 'H2H',
      'spreads': 'Spreads',
      'totals': 'Totals',
      'player_props': 'Props'
    };
    if (selectedMarkets.length > 0) {
      const marketLabels = selectedMarkets.map(key => MARKET_LABELS[key] || key);
      parts.push(marketLabels.join(', '));
    }

    if (selectedBookmakers.length > 0) {
      parts.push(`${selectedBookmakers.length} Bookmaker${selectedBookmakers.length > 1 ? 's' : ''}`);
    } else {
      parts.push('All Bookmakers');
    }

    parts.push(`${minEV}%-${maxEV}% EV`);
    parts.push(`${minProbability}%-${maxProbability}% prob`);

    const statusParts = [];
    if (selectedGameStatus.includes('live')) statusParts.push('Live');
    if (selectedGameStatus.includes('upcoming')) statusParts.push('Upcoming');
    parts.push(statusParts.join(' & '));

    return parts.join(' • ');
  };

  return (
    <div className="ev-opportunities">
      <div className="ev-header">
        <div>
          <h2>+EV Betting Opportunities</h2>
          <p>Find positive expected value bets using sharp lines (Pinnacle 40%, BetOnline 30%, Betfair 30%)</p>
        </div>
        <div className="header-controls">
          <button className="refresh-btn" onClick={handleRefresh} disabled={loading}>
            <svg
              className={loading ? 'refresh-icon spinning' : 'refresh-icon'}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10"></polyline>
              <polyline points="1 20 1 14 7 14"></polyline>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <div className="odds-format-toggle">
            <button
              className={`odds-format-btn ${oddsFormat === 'american' ? 'active' : ''}`}
              onClick={() => setOddsFormat('american')}
            >
              American
            </button>
            <button
              className={`odds-format-btn ${oddsFormat === 'decimal' ? 'active' : ''}`}
              onClick={() => setOddsFormat('decimal')}
            >
              Decimal
            </button>
          </div>
        </div>
      </div>

      {/* Current filters display */}
      <div className="current-filters">
        <div className="current-filters-text">
          <span className="filter-label">Filters:</span> {getActiveFiltersText()}
        </div>
        <button className="filter-icon-btn" onClick={onOpenFilters}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
          Filters
        </button>
      </div>

      {error && (
        <div className="error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', marginRight: '8px' }}>
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
          <button onClick={handleRefresh} style={{ marginLeft: '12px', padding: '4px 8px', background: 'rgba(255,255,255,0.1)', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', cursor: 'pointer', fontSize: '10px', fontWeight: 600 }}>
            Retry
          </button>
        </div>
      )}

      {/* Opportunities List */}
      {loading && opportunities.length === 0 ? (
        <div className="skeleton-table">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton-row">
              <div className="skeleton skeleton-cell skeleton-cell-small"></div>
              <div className="skeleton skeleton-cell skeleton-cell-medium"></div>
              <div className="skeleton skeleton-cell skeleton-cell-large"></div>
              <div className="skeleton skeleton-cell skeleton-cell-medium"></div>
              <div className="skeleton skeleton-cell skeleton-cell-small"></div>
            </div>
          ))}
        </div>
      ) : filteredOpportunities.length > 0 ? (
        <EVTable opportunities={filteredOpportunities} oddsFormat={oddsFormat} userTier={userTier} />
      ) : opportunities.length > 0 && searchTerm ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <div>No matches found</div>
          <div>Try adjusting your search term or filters</div>
        </div>
      ) : !hasManuallySearched ? (
        <div className="empty-state empty-state--minimal">
          <button className="empty-state-btn primary empty-state-btn-large" onClick={handleRefresh}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="empty-state-btn-icon"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="5"></circle>
              <line x1="16" y1="16" x2="21" y2="21"></line>
            </svg>
            <span>Start Search</span>
          </button>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">💰</div>
          <div>No +EV opportunities found</div>
          <div>Adjust your filters to find positive expected value bets</div>
          <div className="empty-state-cta">
            <button className="empty-state-btn" onClick={onOpenFilters}>
              Open Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
